import { onlineManager } from "react-query";
import {
  userModule,
  enterpriseModule,
  resellerModule,
  STATIC_DATA_TYPES,
} from "../providers/RakutenSDK/RakutenSDK";
import sjcl from 'sjcl';

const SECRET_KEY = import.meta.env.VITE_SJCL_KEY;

export function setWithExpiry(key, value, ttl) {
  const now = new Date();
  const item = {
    value,
    expiry: typeof ttl === 'number' && ttl > 0 ? now.getTime() + ttl : null, // null means no expiry
  };
  // Encrypt the stringified item
  const encrypted = sjcl.encrypt(SECRET_KEY, JSON.stringify(item));
  sessionStorage.setItem(key, encrypted);
}

export function getWithExpiry(key) {
  const encrypted = sessionStorage.getItem(key);
  if (!encrypted) return null;
  try {
    const decrypted = sjcl.decrypt(SECRET_KEY, encrypted);
    const item = JSON.parse(decrypted);
    const now = new Date();
    // If expiry is set and expired, remove and return null
    if (item.expiry !== null && now.getTime() > item.expiry) {
      sessionStorage.removeItem(key);
      return null;
    }
    return item.value;
  } catch (e) {
    // If decryption fails, remove the item
    sessionStorage.removeItem(key);
    return null;
  }
}

export function clearCache(key) {
  sessionStorage.removeItem(key);
}

/**
 * Removes an item from sessionStorage that was added using setWithExpiry.
 * @param {string} key - The key of the item to remove.
 */
export function removeWithExpiry(key) {
  sessionStorage.removeItem(key);
}



export async function getAdminUsers() {
  const cachedData = getWithExpiry(STATIC_DATA_TYPES.ADMIN_LIST);
  if (!cachedData) {
    let total = 0;
    let userData = [];
    let data;
    let page = 1;
    let pageSize = 100;
    do {
      if (!onlineManager.isOnline()) {
        return [];
      }
      data = await userModule.requestQueryLink({
        page,
        "page-size": pageSize,
      });
      if (Array.isArray(data?._embedded?.admin_users)) {
        userData = userData.concat(data._embedded.admin_users);
        total += data?._embedded?.admin_users.length;
      }
      page += 1;
    } while (data?._embedded?.total_found > total);

    setWithExpiry(STATIC_DATA_TYPES.ADMIN_LIST, userData, 10); // 10 milliseconds

    return Promise.resolve(userData);
  }

  return Promise.resolve(cachedData);
}

export async function getEnterprises() {
  const cachedData = getWithExpiry(STATIC_DATA_TYPES.ENTERPRISE_LIST);
  if (!cachedData) {
    let total = 0;
    let enterpriseData = [];
    let data;
    let page = 1;
    let pageSize;
    pageSize = 100;
    do {
      if (!onlineManager.isOnline()) {
        return [];
      }
      data = await enterpriseModule.enterprise.requestQueryLink({
        page,
        "page-size": pageSize,
        "sort-key": "name",
        "sort-order": "asc"
      });
      if (Array.isArray(data?._embedded?.enterprises)) {
        enterpriseData = enterpriseData.concat(data._embedded.enterprises);
        total += data?._embedded?.enterprises.length;
      }
      page += 1;
    } while (data?._embedded?.total_found > total);

    setWithExpiry(STATIC_DATA_TYPES.ENTERPRISE_LIST, enterpriseData, 10); // 10 milliseconds

    return Promise.resolve(enterpriseData);
  }

  return Promise.resolve(cachedData);
}

export async function getResellers() {
  const cachedData = getWithExpiry(STATIC_DATA_TYPES.RESELLER_LIST);
  if (!cachedData) {
    let total = 0;
    const resellersData = [];
    let data;
    let page = 1;
    let pageSize;
    pageSize = 100;
    do {
      if (!onlineManager.isOnline()) {
        return [];
      }
      data = await resellerModule.reseller.requestQueryLink({
        page,
        "page-size": pageSize,
      });
      if (Array.isArray(data?._embedded?.resellers)) {
        data?._embedded?.resellers.forEach((dataItem) => {
          resellersData.push(dataItem);
        });
        total += data?._embedded?.resellers.length;
      }
      page += 1;
    } while (data?._embedded?.total_found > total);

    setWithExpiry(STATIC_DATA_TYPES.RESELLER_LIST, resellersData, 10); // 10 milliseconds
    return Promise.resolve(resellersData);
  }

  return Promise.resolve(cachedData);
}
